import { useState, useMemo, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  FileStack,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  User,
  FolderOpen,
  FileText,
  Loader2,
  X,
} from "lucide-react";
import { useRoutedDocuments, useDocumentPreview, type ScannedDocument } from "@/hooks/useDocuments";
import { IntakeDateFilter, type DateFilterValue } from "@/components/intake/IntakeDateFilter";
import { PDFViewer } from "@/components/documents/PDFViewer";
import { format, isToday, isYesterday, startOfToday, endOfToday, startOfDay, isSameDay } from "date-fns";

interface RoutedTodayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDateFilter?: DateFilterValue;
}

const PAGE_SIZE = 10;

const DEFAULT_DATE_FILTER: DateFilterValue = {
  type: "today",
  startDate: startOfToday(),
  endDate: endOfToday(),
};

/** Returns true when the date filter spans more than a single calendar day. */
function isMultiDay(filter: DateFilterValue): boolean {
  if (filter.type === "all") return true;
  if (!filter.startDate || !filter.endDate) return true;
  return !isSameDay(filter.startDate, filter.endDate);
}

/** Format a date label for group headers. */
function formatDateGroupLabel(date: Date): string {
  if (isToday(date)) return `Today \u2014 ${format(date, "MMM d")}`;
  if (isYesterday(date)) return `Yesterday \u2014 ${format(date, "MMM d")}`;
  return format(date, "EEE, MMM d");
}

/** Group documents by calendar day, returning groups in order. */
function groupByDate(docs: ScannedDocument[]): { label: string; date: Date; docs: ScannedDocument[] }[] {
  const groups: Map<string, { label: string; date: Date; docs: ScannedDocument[] }> = new Map();

  for (const doc of docs) {
    const d = new Date(doc.updated_at);
    const key = format(d, "yyyy-MM-dd");
    if (!groups.has(key)) {
      const dayStart = startOfDay(d);
      groups.set(key, { label: formatDateGroupLabel(dayStart), date: dayStart, docs: [] });
    }
    groups.get(key)!.docs.push(doc);
  }

  return Array.from(groups.values());
}

function DetailPanel({ document, onClose }: { document: ScannedDocument; onClose: () => void }) {
  const { data: previewUrl, isLoading: previewLoading } = useDocumentPreview(document.id);

  return (
    <div className="flex flex-col h-full overflow-y-auto relative">
      {/* Close button */}
      <button
        className="absolute top-3 right-3 z-10 p-1.5 rounded-md bg-background/80 border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </button>

      {/* PDF Preview */}
      <div className="flex-1 min-h-0 p-4">
        {previewLoading ? (
          <div className="flex items-center justify-center h-full border rounded-lg bg-muted">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : previewUrl ? (
          <PDFViewer url={previewUrl} className="h-full" />
        ) : (
          <div className="flex flex-col items-center justify-center h-full border rounded-lg bg-muted/50 text-muted-foreground">
            <FileText className="h-10 w-10 mb-2" />
            <p className="text-sm">Preview unavailable</p>
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="px-4 py-3 border-t shrink-0 space-y-2.5">
        {document.client?.name && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Client:</span>
            <span className="font-medium truncate">{document.client.name}</span>
          </div>
        )}
        {document.parse_result?.parsed_date && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Date:</span>
            <span className="font-medium">{document.parse_result.parsed_date}</span>
          </div>
        )}
        {document.parse_result?.doc_type && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Type:</span>
            <Badge variant="outline" className="text-xs">{document.parse_result.doc_type}</Badge>
            {document.parse_result?.parsed_action && (
              <Badge variant="outline" className="text-xs">{document.parse_result.parsed_action}</Badge>
            )}
          </div>
        )}
        {document.parse_result?.confidence_score != null && (
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">Confidence:</span>
            <Badge className={
              document.parse_result.confidence_score >= 0.75 ? "bg-green-500" :
              document.parse_result.confidence_score >= 0.5 ? "bg-amber-500" : "bg-red-500"
            }>
              {Math.round(document.parse_result.confidence_score * 100)}%
            </Badge>
          </div>
        )}
        {document.route?.final_dropbox_path && (
          <>
            <Separator />
            <div className="flex items-start gap-2 text-sm">
              <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="min-w-0">
                <span className="text-muted-foreground">Destination:</span>
                <p className="font-mono text-xs text-muted-foreground mt-0.5 break-all">
                  {document.route.final_dropbox_path}
                </p>
              </div>
            </div>
          </>
        )}
        {document.route?.dropbox_share_url && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-1"
            onClick={() => window.open(document.route!.dropbox_share_url!, "_blank")}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            Open in Dropbox
          </Button>
        )}
      </div>
    </div>
  );
}

export function RoutedTodayDialog({ open, onOpenChange, initialDateFilter }: RoutedTodayDialogProps) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterValue>(initialDateFilter ?? DEFAULT_DATE_FILTER);

  // Sync internal state when dialog opens with a new initialDateFilter
  const prevOpen = useRef(open);
  if (open && !prevOpen.current && initialDateFilter) {
    setDateFilter(initialDateFilter);
    setPage(0);
    setSearch("");
    setSelectedDocId(null);
  }
  prevOpen.current = open;

  const { data: routedDocs, isLoading } = useRoutedDocuments(
    dateFilter.type === "all" ? undefined : { startDate: dateFilter.startDate, endDate: dateFilter.endDate }
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setPage(0);
      setSearch("");
      setSelectedDocId(null);
      setDateFilter(initialDateFilter ?? DEFAULT_DATE_FILTER);
    }
    onOpenChange(nextOpen);
  };

  const handleDateFilterChange = (value: DateFilterValue) => {
    setDateFilter(value);
    setPage(0);
    setSelectedDocId(null);
  };

  const query = search.toLowerCase();
  const filteredDocs = query && routedDocs
    ? routedDocs.filter((doc) =>
        doc.original_filename.toLowerCase().includes(query) ||
        doc.client?.name?.toLowerCase().includes(query) ||
        doc.route?.final_dropbox_path?.toLowerCase().includes(query)
      )
    : routedDocs || [];

  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const paginatedDocs = filteredDocs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const selectedDoc = routedDocs?.find((d) => d.id === selectedDocId) || null;

  const showDateGroups = isMultiDay(dateFilter);

  // Group paginated docs by date for multi-day views
  const dateGroups = useMemo(
    () => (showDateGroups ? groupByDate(paginatedDocs) : null),
    [paginatedDocs, showDateGroups]
  );

  const emptyMessage = search
    ? "No matching documents"
    : dateFilter.type === "today"
    ? "No documents routed today"
    : dateFilter.type === "yesterday"
    ? "No documents routed yesterday"
    : "No routed documents in this range";

  const emptySubtext = search
    ? "Try a different search term"
    : "Documents will appear here after routing";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-7xl w-[95vw] h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Routed Documents
            <div className="ml-2">
              <IntakeDateFilter value={dateFilter} onChange={handleDateFilterChange} />
            </div>
            <Badge variant="outline" className="ml-auto text-sm font-normal">
              {filteredDocs.length} document{filteredDocs.length !== 1 ? "s" : ""}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left pane - List */}
          <div className="w-[45%] flex flex-col border-r min-h-0">
            {/* Search */}
            <div className="relative px-4 py-3 shrink-0">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by filename, client, or path..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9 focus-visible:ring-1 focus-visible:ring-border focus-visible:ring-offset-0"
              />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {isLoading ? (
                <div className="space-y-3 p-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : !filteredDocs.length ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <FileStack className="h-10 w-10 text-muted-foreground/50 mb-3" />
                  <p className="text-sm font-medium">{emptyMessage}</p>
                  <p className="text-xs text-muted-foreground mt-1">{emptySubtext}</p>
                </div>
              ) : showDateGroups && dateGroups ? (
                /* Multi-day: grouped by date */
                <div>
                  {dateGroups.map((group) => (
                    <div key={group.label}>
                      <div className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm px-4 py-1.5 border-b">
                        <span className="text-xs font-medium text-muted-foreground">{group.label}</span>
                      </div>
                      <div className="divide-y">
                        {group.docs.map((doc) => {
                          const isSelected = selectedDocId === doc.id;
                          return (
                            <div
                              key={doc.id}
                              className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-l-2 ${isSelected ? "border-l-green-500 bg-muted" : "border-l-transparent"}`}
                              onClick={() => setSelectedDocId(doc.id)}
                            >
                              <div className="flex items-center gap-2">
                                <FileStack className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span className="text-sm font-medium truncate">{doc.original_filename}</span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 ml-6">
                                {doc.client?.name && (
                                  <span className="text-xs text-muted-foreground truncate">{doc.client.name}</span>
                                )}
                                <span className="text-xs text-muted-foreground ml-auto shrink-0">
                                  {format(new Date(doc.updated_at), "h:mm a")}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Single-day: flat list */
                <div className="divide-y">
                  {paginatedDocs.map((doc) => {
                    const isSelected = selectedDocId === doc.id;
                    return (
                      <div
                        key={doc.id}
                        className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-l-2 ${isSelected ? "border-l-green-500 bg-muted" : "border-l-transparent"}`}
                        onClick={() => setSelectedDocId(doc.id)}
                      >
                        <div className="flex items-center gap-2">
                          <FileStack className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{doc.original_filename}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 ml-6">
                          {doc.client?.name && (
                            <span className="text-xs text-muted-foreground truncate">{doc.client.name}</span>
                          )}
                          <span className="text-xs text-muted-foreground ml-auto shrink-0">
                            {format(new Date(doc.updated_at), "h:mm a")}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t shrink-0">
                <p className="text-xs text-muted-foreground">
                  {safePage * PAGE_SIZE + 1}-{Math.min((safePage + 1) * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={safePage === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="text-xs px-1.5">
                    {safePage + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right pane - Detail */}
          <div className="w-[55%] min-h-0">
            {selectedDoc ? (
              <DetailPanel document={selectedDoc} onClose={() => setSelectedDocId(null)} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-sm">Select a document to preview</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
